<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button } from '@drama/ui'
import { NOVEL_DRAMA_LOCATION_PRESET_LIMIT } from '@drama/contracts/novel'
import NovelCharacterDramaPresets from './NovelCharacterDramaPresets.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import { dramaPresetCounts } from '@drama/novel-authoring'
import './novel-sections.css'

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'navigate'])
const { t } = useI18n()
// Preserve source indexes after excluding the player, so edits and issue paths still target the same actor.
const people = computed(() => (props.modelValue.characters ?? []).map((person, index) => ({ person, index }))
  .filter(({ person }) => person.id !== props.modelValue.player_id))
const counts = computed(() => dramaPresetCounts(props.modelValue))
const global_notes = computed(() => props.issues.filter(issue => issue.path === 'characters').map(issue => ({ key: issue.key, text: issue.note })))
</script>

<template>
  <section
    class="novel-entities"
    data-path="characters"
    data-testid="novel-drama-section"
    tabindex="-1"
  >
    <p class="novel-entities__hint">
      {{ t('novelStudio.dramaPresets.help') }}
    </p>
    <p
      class="novel-drama-settings__counts"
      data-testid="novel-drama-counts"
      role="status"
    >
      {{ t('novelStudio.dramaPresets.counts', { locations: counts.location_count, limit: NOVEL_DRAMA_LOCATION_PRESET_LIMIT, defaults: counts.default_count, total: counts.total_asset_count }) }}
    </p>
    <NovelIssueNote :notes="global_notes" />
    <p class="novel-entities__hint">
      {{ t('novelStudio.dramaPresets.saveHelp') }}
    </p>
    <Button
      variant="secondary"
      size="sm"
      data-testid="novel-drama-edit-characters"
      @click="emit('navigate', 'characters')"
    >
      {{ t('novelStudio.dramaPresets.editCharacters') }}
    </Button>
    <p
      v-if="!people.length"
      class="novel-entities__empty"
    >
      {{ t('novelStudio.dramaPresets.empty') }}
    </p>
    <article
      v-for="{ person, index } in people"
      :key="person.id"
      class="novel-entity"
      :data-testid="`novel-drama-character-${index}`"
    >
      <header class="novel-entity__header">
        <div class="novel-entity__title">
          <h3>{{ person.name || person.id }}</h3>
          <code>{{ person.id }}</code>
        </div>
      </header>
      <NovelCharacterDramaPresets
        :model-value="modelValue"
        :index="index"
        :issues="issues"
        :disabled="disabled"
        @update:model-value="emit('update:modelValue', $event)"
        @navigate="emit('navigate', $event)"
      />
    </article>
  </section>
</template>

<style scoped>
.novel-drama-settings__counts {
  margin: 0;
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
}
</style>
